
    import __i18nConfig from '@next-translate-root/i18n'
    import __loadNamespaces from 'next-translate/loadNamespaces'
    import { IFaq } from '@/types';
import { BannerBlurred } from 'components/common/BannerBlurred';
import { BannerHalfBlurred } from 'components/common/BannerHalfBlurred';
import { Button } from 'components/common/Button';
import { Collapsable } from 'components/common/Collapsable';
import { FeatureCard } from 'components/common/FeatureCard';
import { Icon } from 'components/common/Icon';
import { Markdown } from 'components/common/Markdown';
import { Meta } from 'components/common/Meta';
import { BannerContent } from 'components/how-it-works/BannerContent';
import routes from 'constants/routes';
import { BaseLayout } from 'layouts/BaseLayout';
import { GetStaticProps } from 'next';
import useTranslation from 'next-translate/useTranslation';
import Image from 'next/image';
import { ReactElement, useEffect, useState } from 'react';
import { API_BASEPATH } from '../../api';
const HowItWorksArtists = () => {
    const { t } = useTranslation('how_it_works');
    const [faqs, setFaqs] = useState<IFaq[]>();
    const getFaqs = async () => {
        const response = await fetch(`${API_BASEPATH}/api/faqs`);
        const data = await response.json();
        const howItWorksFaqs = data.data?.filter((faq: IFaq) => faq.attributes.page === 'ARTISTS');
        setFaqs(howItWorksFaqs);
    };
    useEffect(() => {
        getFaqs();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);
    return (<>
      <Meta pageTitle={t('artists_page_title')} pageDescription={t('artists_page_desc')} imageName='social-hiw-royalties' relativeUrl={routes.howItWorks.forArtists}/>

      <main className='bg-warm-50'>
        <BannerHalfBlurred src='/images/banners/hiw-artists.png' priority reversed loading='eager'>
          <BannerContent title={t('artists_banner_title')} description={t('artists_banner_description_1')}/>
        </BannerHalfBlurred>

        <div className='w-full bg-cool-50 px-6 py-10 md:px-20 md:py-16'>
          <p className='mx-auto max-w-[1120px] text-center text-sm text-cool-800 sm:text-base md:text-xl'>
            <span className='font-bold'>
              {t('for')} <u>{t('every_sale')}</u>
              {t('25%')}
            </span>
            <br /> <br />
            {t('no_matter_how_many_times_a_work_is_sold')}
          </p>
        </div>

        <div className='relative flex w-full items-center justify-center bg-warm-50 lg:bg-cool-50'>
          <Image alt='royalties' src='/files/artist-royalties-small.jpg' quality={100} width={768} height={468} className='max-h-[468px] object-contain md:hidden'/>

          <Image src='/files/artist-royalties-large.jpg' alt='royalties' quality={100} className='hidden max-h-[773px] object-contain md:block lg:rounded-lg' width={1280} height={773} priority/>
        </div>

        <div className='mb-8 w-full bg-cool-50 px-6 py-12 md:px-20 md:py-16'>
          <p className='mx-auto max-w-[1120px] text-center text-base font-medium text-cool-800 md:text-2xl'>
            {t('it_may_seem_counterintuitive')}
          </p>
        </div>

        <div className='container max-w-[1120px]'>
          <div className='mb-4 md:px-8 md:py-8 lg:px-10 lg:py-10'>
            <p className='text-left text-lg font-medium text-cool-1000 md:hidden'>
              {t('why_should_artists')}
            </p>
            <h5 className='hidden font-body font-medium text-cool-1000 md:block'>
              {t('why_should_artists')}
            </h5>
            <p className='mt-4 text-sm text-cool-600 md:text-base'>
              {t('the_art_world')} <br /> <br /> {t('think_of')} <br /> <br />{' '}
              <span className='italic'>{t('outside_of_live')}</span>{' '}
              <span className='font-bold'>{t('hank')}</span>
            </p>
          </div>

          <div className='mb-20 grid grid-cols-1 items-start gap-y-4 lg:grid-cols-3 lg:gap-x-4 lg:gap-y-0'>
            <FeatureCard className='col-span-1 h-full' iconName='filled-money' title={t('feature_cards_artists.1_title')} shortDescription={<p className='text-sm text-cool-600 md:text-base'>
                  {t('feature_cards_artists.1_description')}
                </p>} longDescription={<p className='text-sm text-cool-600 md:text-base'>
                  {t('feature_cards_artists.1_long_description_1')}
                </p>}/>
            <FeatureCard className='col-span-1 h-full' iconName='filled-money' title={t('feature_cards_artists.2_title')} shortDescription={<p className='text-sm text-cool-600 md:text-base'>
                  {t('feature_cards_artists.2_description')}
                </p>} longDescription={<p className='text-sm text-cool-600 md:text-base'>
                  {t('feature_cards_artists.2_long_description_1')}
                </p>}/>
            <FeatureCard className='col-span-1 h-full' iconName='filled-circular-arrows' title={t('feature_cards_artists.3_title')} shortDescription={<p className='text-sm text-cool-600 md:text-base'>
                  {t('feature_cards_artists.3_description')}
                </p>} longDescription={<p className='text-sm text-cool-600 md:text-base'>
                  {t('feature_cards_artists.3_long_description_1')}
                </p>}/>
          </div>
        </div>

        <section className='bg-warm-50 pb-16'>
          <div className='container flex justify-center'>
            <div className='w-full max-w-[1120px]'>
              <p className='mb-2 text-center text-sm text-cool-600 sm:text-lg md:text-xl'>
                {t('generation')}
              </p>
              <p className='mb-16 text-center text-lg font-medium text-cool-800 sm:text-2xl md:mb-20 md:text-3xl'>
                {t('we_think')}
              </p>
              <div className='grid grid-cols-1 space-y-4 sm:hidden'>
                <div className='flex flex-col space-y-4 rounded-lg border border-solid border-cool-300 bg-cool-50 px-6 py-5'>
                  <p className='text-sm italic text-cool-700'>
                    {t('artists_quotes.1_quote')}
                  </p>
                  <div className='flex items-center space-x-3'>
                    <div className='relative min-h-[64px] min-w-[64px] overflow-hidden rounded-full'>
                      <Image layout='fill' src='/files/robert-rauschenberg-small.png' alt='Robert Rauschenberg' objectFit='contain' objectPosition='center' quality={100}/>
                    </div>
                    <p className='text-lg font-medium'>
                      {t('artists_quotes.1_name')}
                    </p>
                  </div>
                </div>

                <div className='flex flex-col space-y-4 rounded-lg border border-solid border-cool-300 bg-cool-50 px-6 py-5'>
                  <p className='text-sm italic text-cool-700'>
                    {t('artists_quotes.2_quote')}
                  </p>
                  <div className='flex items-center space-x-3'>
                    <div className='relative min-h-[64px] min-w-[64px] overflow-hidden rounded-full'>
                      <Image layout='fill' src='/files/nina-chanel-small.png' alt='Nina Chanel' objectFit='contain' objectPosition='center' quality={100}/>
                    </div>
                    <p className='text-lg font-medium'>
                      {t('artists_quotes.2_name')}
                    </p>
                  </div>
                </div>

                <div className='flex flex-col space-y-4 rounded-lg border border-solid border-cool-300 bg-cool-50 px-6 py-5'>
                  <p className='text-sm italic text-cool-700'>
                    {t('artists_quotes.3_quote')}
                  </p>
                  <div className='flex items-center space-x-3'>
                    <div className='relative min-h-[64px] min-w-[64px] overflow-hidden rounded-full'>
                      <Image layout='fill' src='/files/chuck-close-small.png' alt='Chuck Close' objectFit='cover' objectPosition='center' quality={100}/>
                    </div>
                    <p className='text-lg font-medium'>
                      {t('artists_quotes.3_name')}
                    </p>
                  </div>
                </div>

                <div className='flex flex-col space-y-4 rounded-lg border border-solid border-cool-300 bg-cool-50 px-6 py-5'>
                  <p className='text-sm italic text-cool-700'>
                    {t('artists_quotes.4_quote')}
                  </p>
                  <div className='flex items-center space-x-3'>
                    <div className='relative min-h-[64px] min-w-[64px] overflow-hidden rounded-full'>
                      <Image layout='fill' src='/files/frank-stella-small.png' alt='Frank Stella' objectFit='contain' objectPosition='center' quality={100}/>
                    </div>
                    <p className='text-lg font-medium'>
                      {t('artists_quotes.4_name')}
                    </p>
                  </div>
                </div>
              </div>

              <div className='-mx-2 -my-2 hidden grid-cols-1 sm:grid xl:grid-cols-2'>
                <div className='mx-2 my-2 flex min-h-[168px] rounded-lg border border-solid border-cool-300 bg-cool-50 xl:min-h-[221px]'>
                  <div className='relative h-auto min-w-[180px]'>
                    <Image layout='fill' src='/files/robert-rauschenberg.png' className='!rounded-l-lg' objectFit='cover' objectPosition='center' alt='Robert Rauschenberg' quality={100}/>
                  </div>
                  <div className='flex flex-col justify-between p-8'>
                    <p className='italic text-cool-700'>
                      {t('artists_quotes.1_quote')}
                    </p>
                    <h6 className='font-body text-xl font-medium'>
                      {t('artists_quotes.1_name')}
                    </h6>
                  </div>
                </div>

                <div className='mx-2 my-2 flex min-h-[168px] rounded-lg border border-solid border-cool-300 bg-cool-50 xl:min-h-[221px]'>
                  <div className='relative h-auto min-w-[180px]'>
                    <Image layout='fill' src='/files/nina-chanel.png' className='!rounded-l-lg' objectFit='cover' objectPosition='center' alt='Nina Chanel' quality={100}/>
                  </div>
                  <div className='flex flex-col justify-between p-8'>
                    <p className='italic text-cool-700'>
                      {t('artists_quotes.2_quote')}
                    </p>
                    <h6 className='font-body text-xl font-medium'>
                      {t('artists_quotes.2_name')}
                    </h6>
                  </div>
                </div>

                <div className='mx-2 my-2 flex min-h-[168px] rounded-lg border border-solid border-cool-300 bg-cool-50 xl:min-h-[221px]'>
                  <div className='relative h-auto min-w-[180px]'>
                    <Image layout='fill' src='/files/chuck-close.png' objectFit='cover' className='hidden !rounded-l-lg xl:block' alt='Chuck Close' quality={100}/>
                    <Image layout='fill' src='/files/chuck-close-medium.png' objectFit='cover' alt='Chuck Close' quality={100} className='hidden !rounded-l-lg sm:block xl:hidden'/>
                  </div>
                  <div className='flex flex-col justify-between p-8'>
                    <p className='italic text-cool-700'>
                      {t('artists_quotes.3_quote')}
                    </p>
                    <h6 className='font-body text-xl font-medium'>
                      {t('artists_quotes.3_name')}
                    </h6>
                  </div>
                </div>

                <div className='mx-2 my-2 flex min-h-[168px] rounded-lg border border-solid border-cool-300 bg-cool-50 xl:min-h-[221px]'>
                  <div className='relative h-auto min-w-[180px]'>
                    <Image layout='fill' src='/files/frank-stella.png' className='!rounded-l-lg' objectFit='cover' objectPosition='center' alt='Frank Stella' quality={100}/>
                  </div>
                  <div className='flex flex-col justify-between p-8'>
                    <p className='italic text-cool-700'>
                      {t('artists_quotes.4_quote')}
                    </p>
                    <h6 className='font-body text-xl font-medium'>
                      {t('artists_quotes.4_name')}
                    </h6>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        <section className='w-full bg-cool-50 px-6 pb-16 pt-12 text-center'>
          <div className='mb-6 flex justify-center'>
            <Icon name='icon-charity' custom size={80}/>
          </div>

          <h5 className='font-body text-2xl font-medium tracking-normal md:text-3xl'>
            {t('charity_releases')}
          </h5>

          <h6 className='mt-2 font-body text-sm font-normal tracking-normal text-cool-600 md:text-lg'>
            {t('the_gift_that_keeps_on_giving')}
          </h6>

          <p className='mt-10 whitespace-pre-line text-sm text-cool-600 md:text-base'>
            {t('the_growing_number_of_charity')}
          </p>

          <p className='mt-10 font-medium text-cool-800 md:text-xl'>
            {t('on_fairart_the_artists_initial_donation')}
          </p>
        </section>

        <div className='relative mb-16 flex w-full items-center justify-center bg-warm-50 lg:bg-cool-50 lg:pb-20'>
          <Image alt='royalties' src='/files/charity-donation-small.jpg' quality={100} width={768} height={447} className='max-h-[447px] object-contain md:hidden'/>

          <Image src='/files/charity-donation-large.jpg' alt='royalties' quality={100} className='hidden max-h-[773px] object-contain md:block lg:rounded-lg' width={1280} height={773}/>
        </div>

        {faqs && faqs.length > 0 && (<section className='bg-warm-50 pb-20 md:pb-[72px]'>
            <h5 className='text-center font-medium md:hidden'>FAQs</h5>
            <h4 className='hidden text-center font-medium md:block'>FAQs</h4>
            <div className='container mt-8 flex justify-center'>
              <div className='flex w-full max-w-[1120px] flex-col items-center justify-center space-y-4'>
                {faqs.map((faq, index) => (<Collapsable key={index} type='faq' className='w-full rounded-xl border border-solid border-cool-300 bg-cool-50 p-5 sm:p-8' title={faq.attributes.title ? (<Markdown className='font-medium sm:text-xl'>
                          {faq.attributes.title}
                        </Markdown>) : (<p>
                          Sorry, we don&apos;t have a question for this answer
                          yet. Try again later!
                        </p>)}>
                    <div className='mt-3 text-sm text-cool-600 sm:mt-4 sm:text-base'>
                      {faq.attributes.description ? (<Markdown className='custom-answer'>
                          {faq.attributes.description}
                        </Markdown>) : (<p>
                          Sorry, we don&apos;t have an answer yet. Try again
                          later!
                        </p>)}
                    </div>
                  </Collapsable>))}
              </div>
            </div>
          </section>)}

        <BannerBlurred src='/files/banners/bg-artworks-horizontal-1.jpg' priority={false} loading='lazy' quality={50}>
          <p className='text-lg text-cool-700'>
            {t('common:what_are_you_waiting_for')}
          </p>
          <h5 className='mt-2 text-center text-2xl font-medium text-cool-900 lg:text-3xl'>
            {t('common:buy_and_sell_from_artists')}
          </h5>
          <Button size='lg' type='link' href='/artists' className='mt-8'>
            {t('common:view_all_artists')}
          </Button>
        </BannerBlurred>
      </main>
    </>);
};
HowItWorksArtists.getLayout = (page: ReactElement) => (<BaseLayout style='off-white-bg'>{page}</BaseLayout>);
const getStaticProps: GetStaticProps = async () => ({ props: {} });
export default HowItWorksArtists;

    async function __Next_Translate__getStaticProps__196af22db66__(ctx) {
      const res = await getStaticProps(ctx)
      return {
        
        ...res,
        props: {
          ...(res.props || {}),
          ...(await __loadNamespaces({
            ...ctx,
            ...__i18nConfig,
            pathname: '/how-it-works/for-artists',
            loaderName: 'getStaticProps',
            loadLocaleFrom: __i18nConfig && __i18nConfig.loadLocaleFrom || ((l, n) => import(`@next-translate-root/locales/${l}/${n}`).then(m => m.default)),
          }))
        }
      }
    }
    export { __Next_Translate__getStaticProps__196af22db66__ as getStaticProps }
  